import {
  CopilotCallbacks,
  StartGameCallback,
  StopGameCallback,
  PauseGameCallback,
  UserAddedCallback,
  UserRemovedCallback,
  CommandToWidgetCallback,
  ExceptionCallback
} from './CopilotCallbacks';

export class CopilotServiceCallbacks implements CopilotCallbacks {
  // Required Callbacks
  private _startCallback: StartGameCallback;
  private _stopCallback: StopGameCallback;
  private _pauseCallback: PauseGameCallback;
  private _userAddedCallback: UserAddedCallback;
  private _userRemovedCallback: UserRemovedCallback;
  private _commandToWidgetCallback: CommandToWidgetCallback;

  // Optional Callbacks
  private _exceptionCallback: ExceptionCallback;

  /**
   * Constructor
   * @param startCallback - called when the widget recieves startActivity command
   *          Signature: (onCompleteCallback:Function, activityDefinition:ActivityDefinition, details:any)
   * @param stopCallback - called when widget recieves stopActivity command
   *          Signature: (onCompleteCallback:Function, details:any)
   * @param pauseCallback - called when widget recieves pause command
   *          Signature: (onCompleteCallback:Function, paused:Boolean, details:any)
   * @param userAddedCallback - called when widget recieves addUser command
   *          Signature: (onCompleteCallback:Function, user:User, details:any)
   * @param userRemovedCallback - called when widget recieves removeUser command
   *          Signature: (onCompleteCallback:Function, user:User, details:any)
   * @param commandToWidgetCallback - called when widget recieves commandToWidget command
   *          Signature: (onCompleteCallback:Function, command:String, args:String)
   */
  public constructor(
    startCallback: StartGameCallback,
    stopCallback: StopGameCallback,
    pauseCallback: PauseGameCallback,
    userAddedCallback: UserAddedCallback,
    userRemovedCallback: UserRemovedCallback,
    commandToWidgetCallback: CommandToWidgetCallback
  ) {
    //set required callbacks
    this._startCallback = startCallback;
    this._stopCallback = stopCallback;
    this._pauseCallback = pauseCallback;
    this._userAddedCallback = userAddedCallback;
    this._userRemovedCallback = userRemovedCallback;
    this._commandToWidgetCallback = commandToWidgetCallback;

    //set optional callbacks
    this._exceptionCallback = () => void 0;
  }

  /**
   * ---------------------------------------------------------------------------------------------------------------------------
   * Required Callbacks
   * ---------------------------------------------------------------------------------------------------------------------------
   **/

  /**
   * Returns the start game callback.
   */
  get startCallback(): StartGameCallback {
    return this._startCallback;
  }

  /**
   * Sets the start game callback to be the given value (cannot be null).
   * Function should have the following signature: (onCompleteCallback:Function, activityDefinition:any, details:any)
   */
  set startCallback(value: StartGameCallback) {
    if (value != null) {
      this._startCallback = value;
    }
  }

  /**
   * Returns the stop game callback.
   */
  get stopCallback(): StopGameCallback {
    return this._stopCallback;
  }

  /**
   * Sets the stop game callback to be the given value (cannot be null).
   * Function should have the following signature: (onCompleteCallback:Function, details:any)
   */
  set stopCallback(value: StopGameCallback) {
    if (value != null) {
      this._stopCallback = value;
    }
  }

  /**
   * Returns the pause game callback.
   */
  get pauseCallback(): PauseGameCallback {
    return this._pauseCallback;
  }

  /**
   * Sets the pause game callback to be the given value (cannot be null).
   * Function should have the following signature: (onCompleteCallback:Function, paused:Boolean, details:any)
   */
  set pauseCallback(value: PauseGameCallback) {
    if (value != null) {
      this._pauseCallback = value;
    }
  }

  /**
   * Returns the user added callback.
   */
  get userAddedCallback(): UserAddedCallback {
    return this._userAddedCallback;
  }

  /**
   * Sets the user added callback to be the given value.
   * Function should have the following signature: (onCompleteCallback:Function, user:User, details:any)
   */
  set userAddedCallback(value: UserAddedCallback) {
    this._userAddedCallback = value;
  }

  /**
   * Returns the user removed callback.
   */
  get userRemovedCallback(): UserRemovedCallback {
    return this._userRemovedCallback;
  }

  /**
   * Sets the user removed callback to be the given value.
   * Function should have the following signature: (onCompleteCallback:Function, user:User, details:any)
   */
  set userRemovedCallback(value: UserRemovedCallback) {
    this._userRemovedCallback = value;
  }

  /**
   * Returns the command callback.
   */
  get commandToWidgetCallback(): CommandToWidgetCallback {
    return this._commandToWidgetCallback;
  }

  /**
   * Sets the command callback to be the given value.
   * Function should have the following signature: (onCompleteCallback:Function, command:String, args:String)
   */
  set commandToWidgetCallback(value: CommandToWidgetCallback) {
    this._commandToWidgetCallback = value;
  }

  /**
   * ---------------------------------------------------------------------------------------------------------------------------
   * Optional Callbacks
   * ---------------------------------------------------------------------------------------------------------------------------
   **/

  /**
   * Returns the exception callback
   */
  get exceptionCallback(): ExceptionCallback {
    return this._exceptionCallback;
  }

  /**
   * Sets the exception callback to the given value
   * Function should have the following signature: (error:Error)
   */
  set exceptionCallback(value: ExceptionCallback) {
    this._exceptionCallback = value;
  }
}
